import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import {
  NoteAdd as NoteAddIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Edit as EditIcon
} from '@mui/icons-material';

/**
 * ClinicalNotepad Component
 * 
 * This component provides a notepad for doctors to write and view clinical notes for each patient.
 * 
 * @param {Object} props
 * @param {string} props.patientId - The ID of the patient
 * @param {string} props.consultationId - The ID of the consultation
 */
const ClinicalNotepad = ({ patientId, consultationId }) => {
  const theme = useTheme();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [textFieldRows, setTextFieldRows] = useState(4);
  
  // Current doctor info (in a real app, this would come from authentication)
  const currentDoctor = {
    id: 'dr-sarah-wilson',
    name: 'Dr. Sarah Wilson'
  };

  // Fetch existing notes when the component mounts
  useEffect(() => {
    const fetchNotes = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // In a real implementation, this would be an API call to fetch notes
        // For now, we'll use mock data
        setTimeout(() => {
          // Mock data for demonstration
          const mockNotes = [
            {
              id: '1',
              text: 'Patient presents with symptoms consistent with migraine. Recommended rest in a dark room and prescribed sumatriptan 50mg PRN.',
              createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
              doctorId: 'dr-sarah-wilson',
              doctorName: 'Dr. Sarah Wilson'
            },
            {
              id: '2',
              text: 'Follow-up: Patient reports improvement with medication. Headaches reduced in frequency from daily to twice weekly. Continue current treatment plan.',
              createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
              doctorId: 'dr-sarah-wilson',
              doctorName: 'Dr. Sarah Wilson'
            },
            {
              id: '3',
              text: 'Patient referred to neurology for further evaluation. MRI scheduled for next week to rule out structural causes.',
              createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
              doctorId: 'dr-john-doe',
              doctorName: 'Dr. John Doe'
            }
          ];
          
          setNotes(mockNotes);
          setLoading(false);
        }, 1000);
      } catch (err) {
        console.error('Error fetching notes:', err);
        setError('Failed to load clinical notes. Please try again.');
        setLoading(false);
      }
    };
    
    // Only check for consultationId since patientId might not be available in the mock data
    if (consultationId) {
      fetchNotes();
    } else {
      setLoading(false);
      setError('Consultation ID is required to load notes.');
    }
  }, [consultationId]);

  // Handle text field auto-expansion
  const handleNoteChange = (e) => {
    const text = e.target.value;
    setNewNote(text);
    
    // Count the number of line breaks
    const lineBreaks = (text.match(/\n/g) || []).length;
    
    // Set rows based on content, with a minimum of 4 and maximum of 20
    const calculatedRows = Math.min(Math.max(4, lineBreaks + 2), 20);
    setTextFieldRows(calculatedRows);
  };

  // Handle saving a new note
  const handleSaveNote = async () => {
    if (!newNote.trim()) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // In a real implementation, this would be an API call to save the note
      // For now, we'll simulate it
      setTimeout(() => {
        const newNoteObj = {
          id: Date.now().toString(),
          text: newNote,
          createdAt: new Date().toISOString(),
          doctorId: currentDoctor.id,
          doctorName: currentDoctor.name
        };
        
        setNotes(prevNotes => [newNoteObj, ...prevNotes]);
        setNewNote('');
        setTextFieldRows(4); // Reset rows to default
        setSaving(false);
      }, 1000);
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save clinical note. Please try again.');
      setSaving(false);
    }
  };

  // Check if the doctor can edit a note
  const canEditNote = (note) => {
    return note.doctorId === currentDoctor.id;
  };

  // Handle editing a note
  const handleEditNote = (noteId) => {
    const noteToEdit = notes.find(note => note.id === noteId);
    if (noteToEdit && canEditNote(noteToEdit)) {
      setEditingNoteId(noteId);
      setEditingNoteText(noteToEdit.text);
    }
  };

  // Handle saving an edited note
  const handleSaveEditedNote = async () => {
    if (!editingNoteText.trim() || !editingNoteId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // In a real implementation, this would be an API call to update the note
      // For now, we'll simulate it
      setTimeout(() => {
        setNotes(prevNotes => 
          prevNotes.map(note => 
            note.id === editingNoteId 
              ? { ...note, text: editingNoteText, updatedAt: new Date().toISOString() } 
              : note
          )
        );
        
        setEditingNoteId(null);
        setEditingNoteText('');
        setSaving(false);
      }, 1000);
    } catch (err) {
      console.error('Error updating note:', err);
      setError('Failed to update clinical note. Please try again.');
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  if (loading && notes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading clinical notes...
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 0 }}>
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <NoteAddIcon sx={{ mr: 1 }} />
          Clinical Notes
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={textFieldRows}
          placeholder="Write a new clinical note..."
          value={newNote}
          onChange={handleNoteChange}
          disabled={saving}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 2000 }} // Reasonable character limit
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {newNote.length > 0 ? `${newNote.length}/2000 characters` : ''}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveNote}
            disabled={!newNote.trim() || saving}
          >
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </Box>
      </Box>

      <Divider />

      <List sx={{ p: 0 }}>
        {notes.length === 0 ? (
          <ListItem>
            <ListItemText 
              primary="No clinical notes yet" 
              secondary="Write your first note above"
              sx={{ textAlign: 'center', py: 2 }}
            />
          </ListItem>
        ) : (
          notes.map((note) => (
            <ListItem 
              key={note.id} 
              alignItems="flex-start"
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: note.id === editingNoteId ? alpha(theme.palette.primary.main, 0.05) : 'transparent'
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: note.doctorId === currentDoctor.id ? theme.palette.primary.main : theme.palette.grey[500] }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              
              {note.id === editingNoteId ? (
                <Box sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={Math.min(Math.max(4, (note.text.match(/\n/g) || []).length + 2), 20)}
                    value={editingNoteText}
                    onChange={(e) => setEditingNoteText(e.target.value)}
                    disabled={saving}
                    sx={{ mb: 1 }}
                    inputProps={{ maxLength: 2000 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {editingNoteText.length}/2000 characters
                    </Typography>
                    <Box>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingNoteText('');
                        }}
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveEditedNote}
                        disabled={!editingNoteText.trim() || saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {note.doctorName}
                        {note.doctorId === currentDoctor.id && ' (You)'}
                      </Typography>
                      
                      {canEditNote(note) && (
                        <Tooltip title="Edit note">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditNote(note.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        sx={{ 
                          display: 'block', 
                          my: 1,
                          whiteSpace: 'pre-wrap' // Preserve line breaks
                        }}
                      >
                        {note.text}
                      </Typography>
                      
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <AccessTimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                        {formatDate(note.createdAt)}
                        {note.updatedAt && ` (Edited: ${formatDate(note.updatedAt)})`}
                      </Typography>
                    </>
                  }
                />
              )}
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );
};

export default ClinicalNotepad; 